import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface AdminChallengeQueryFilters {
  status?: ChallengeService.ChallengeStatusEnum[]
  feedbackStatus?: ChallengeService.FeedbackStatusFilterOption[]
  userId?: string[]
  taskId?: string[]
  assignmentIds?: string[]
  slug?: string
}

export interface UseAdminChallengesParams {
  queryFilters: AdminChallengeQueryFilters
  sortField?: keyof ChallengeService.Challenge
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminChallengesQueryKeys = {
  all: () => ['admin-challenges'] as const,
  list: (params: { queryFilters: AdminChallengeQueryFilters; sortField?: keyof ChallengeService.Challenge; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-challenges', params] as const,
}

const fetchAdminChallengesPage = async ({
  pageParam,
  queryFilters,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  queryFilters: AdminChallengeQueryFilters
  sortField?: keyof ChallengeService.Challenge
  sortDirection?: 'asc' | 'desc'
}): Promise<ChallengeService.ChallengeInfo[]> => {
  const { limit, offset } = pageParam

  const request: ChallengeService.ListChallengesRequest = {
    filter: {
      status: queryFilters.status?.length ? queryFilters.status : undefined,
      feedbackStatus: queryFilters.feedbackStatus?.length ? queryFilters.feedbackStatus : undefined,
      userId: queryFilters.userId?.length ? queryFilters.userId : undefined,
      taskId: queryFilters.taskId?.length ? queryFilters.taskId : undefined,
      assignmentIds: queryFilters.assignmentIds?.length ? queryFilters.assignmentIds : undefined,
      slug: queryFilters.slug || undefined,
    },
    sortField: sortField as ChallengeService.ChallengeSortFieldEnum | undefined,
    sortDirection: sortDirection as ChallengeService.SortDirectionEnum | undefined,
    limit,
    offset,
  }

  const response = await apis.challengeService.listChallenges(request)
  return response.data
}

export const useAdminChallenges = ({
  queryFilters,
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminChallengesParams) => {
  const query = useInfiniteQuery({
    queryKey: adminChallengesQueryKeys.list({ queryFilters, sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminChallengesPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        queryFilters,
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage || lastPage.length === 0 || lastPageParam?.limit > lastPage.length) {
        return undefined
      }

      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit =
        allPages.length === 1
          ? initialPageSize * 2
          : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)

      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const challenges = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    challenges,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}

